import React, { useEffect, useState } from 'react';
import {
  Badge, Box, Button, Flex, FormControl, FormHelperText, FormLabel, Input, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, VStack,
  useColorModeValue, useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';
import { money } from '../utils/money';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';
import { useConfirm } from './ConfirmProvider';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

interface WriteOffHistoryRow {
  id: string;
  amount: number;
  reason: string;
  createdDate: string;
  createdBy?: string | null;
  reversedDate?: string | null;
  reversedBy?: string | null;
  entered: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string | null;
  invoiceNumber: string;
  openBalance: number; // RetailTotal — already net of earlier write-offs
  isPaid: boolean;
}

// item 1564 (Chad 2026-10-12: "partial yes, commissions follow collected") — write off part
// or all of an invoice's OPEN balance. The lines are never edited: the invoice total nets the
// write-off (AR / statements / pay links read the agreed amount), the accountant gets a
// WRITE-OFF row in Enter to QB, commissions scale to what's collected, and Stripe's copy is
// adjusted. Undo is a soft reverse (an entered write-off gets its own "reverse it in QB" row).
const WriteOffInvoiceModal: React.FC<Props> = ({
  isOpen, onClose, invoiceId, invoiceNumber, openBalance, isPaid,
}) => {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const sub = useColorModeValue('gray.600', 'whiteAlpha.700');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) { setAmount(openBalance > 0 ? openBalance.toFixed(2) : ''); setReason(''); }
  }, [isOpen, openBalance]);

  const { data: history, refetch } = useQuery(['invoice-write-offs', invoiceId], async () => {
    const r = await axios.get(`${apiUrl()}AccountantDesk/write-offs/${invoiceId}`);
    return r.data as WriteOffHistoryRow[];
  }, { enabled: isOpen && !!invoiceId, retry: false });

  const refreshAll = () => {
    qc.invalidateQueries('invoices');
    qc.invalidateQueries('project');
    qc.invalidateQueries('accountant-adjustments');
    refetch();
  };

  const value = Number(amount);
  const valid = value > 0 && value <= openBalance + 0.0001 && reason.trim().length > 0;
  const whole = valid && Math.abs(value - openBalance) < 0.005;

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${apiUrl()}AccountantDesk/write-off`, { invoiceId, amount: value, reason: reason.trim() });
      toast({
        status: 'success',
        title: whole ? `${invoiceNumber} written off` : `${money(value)} written off ${invoiceNumber}`,
        description: whole ? 'It leaves AR; the accountant gets a row to match QuickBooks.'
          : `${money(openBalance - value)} is still owed; the accountant gets a row to match QuickBooks.`,
      });
      refreshAll();
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Write-off failed', description: e?.response?.data ?? 'Nothing was changed.' });
    } finally {
      setBusy(false);
    }
  };

  const undo = async (row: WriteOffHistoryRow) => {
    const ok = await confirm(`Undo the ${money(row.amount)} write-off? The amount goes back on ${invoiceNumber}'s balance${row.entered ? ' and the accountant gets a row to reverse it in QuickBooks' : ''}.`, { confirmLabel: 'Undo write-off' });
    if (!ok) return;
    try {
      await axios.post(`${apiUrl()}AccountantDesk/write-off/${row.id}/reverse`, {});
      toast({ status: 'success', title: 'Write-off undone' });
      refreshAll();
    } catch (e: any) {
      toast({ status: 'error', title: 'Undo failed', description: e?.response?.data ?? 'Nothing was changed.' });
    }
  };

  const live = (history ?? []).filter((h) => !h.reversedDate);
  const past = (history ?? []).filter((h) => h.reversedDate);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{`Write off — ${invoiceNumber}`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {isPaid || openBalance <= 0 ? (
              <Text color={sub}>There&apos;s no open balance to write off on this invoice.</Text>
            ) : (
              <>
                <Text fontSize="sm" color={sub}>
                  {`Open balance ${money(openBalance)}. Write off all of it, or just the part you've agreed to forgive — the rest stays owed.`}
                </Text>
                <FormControl isRequired>
                  <FormLabel>Amount to write off</FormLabel>
                  <Input type="number" step="0.01" min={0} max={openBalance} value={amount} onChange={(e) => setAmount(e.target.value)} />
                  <FormHelperText>
                    {valid && !whole ? `${money(openBalance - value)} stays owed.` : 'Defaults to the whole open balance.'}
                  </FormHelperText>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Reason</FormLabel>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why it's being written off" rows={2} />
                  <FormHelperText>The accountant sees this on their QuickBooks row.</FormHelperText>
                </FormControl>
                <Text fontSize="xs" color={sub}>
                  Commissions on this invoice scale to what&apos;s collected. The pay link and Stripe&apos;s copy are adjusted to match.
                </Text>
              </>
            )}

            {(live.length > 0 || past.length > 0) && (
              <Box>
                <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" mb={2}>WRITE-OFFS ON THIS INVOICE</Text>
                <VStack align="stretch" spacing={2}>
                  {[...live, ...past].map((h) => (
                    <Flex key={h.id} justify="space-between" align="center" opacity={h.reversedDate ? 0.55 : 1}>
                      <Box minW={0}>
                        <Text fontWeight="semibold">
                          {money(h.amount)}
                          {h.reversedDate && <Badge ml={2} fontSize="9px">UNDONE</Badge>}
                        </Text>
                        <Text fontSize="xs" color={sub} noOfLines={2}>
                          {[h.reason, h.createdBy, SqlDateTimeToDisplay(h.createdDate)].filter(Boolean).join(' · ')}
                        </Text>
                      </Box>
                      {!h.reversedDate && <Button size="xs" variant="outline" onClick={() => undo(h)}>Undo</Button>}
                    </Flex>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
          {!isPaid && openBalance > 0 && (
            <Button colorScheme="red" onClick={submit} isLoading={busy} isDisabled={!valid}>
              {whole ? 'Write off whole balance' : 'Write off'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WriteOffInvoiceModal;
